/**
 * Frontend helper for calling Claude through the ai-complete Edge Function.
 *
 * Mirrors src/lib/email.ts in shape: typed wrapper around
 * supabase.functions.invoke('ai-complete', ...) returning {data, error}.
 *
 * Usage — single-shot prompt (the most common case in AIHub panels):
 *   const { data, error } = await complete({
 *     prompt: 'Summarize this conversation: ...',
 *     system: 'You are a customer support assistant.',
 *   })
 *   if (error) { ... }
 *   const text = data!.text
 *
 * Usage — multi-turn chat (FreeChat, AIButton):
 *   const { data, error } = await complete({
 *     messages: [
 *       { role: 'user', content: 'Hi' },
 *       { role: 'assistant', content: 'Hello!' },
 *       { role: 'user', content: 'What can you help with?' },
 *     ],
 *   })
 */

import { supabase } from './supabase'

export type AIMessage = {
  role: 'user' | 'assistant'
  content: string
}

export type CompleteInput = {
  /** Single-prompt shorthand. Becomes a single user message server-side. */
  prompt?: string
  /** Explicit message history for multi-turn conversations. */
  messages?: AIMessage[]
  /** Optional system prompt override. */
  system?: string
  /** Optional max output tokens. Capped at 4000 server-side. */
  max_tokens?: number
}

export type CompleteResult = {
  /** Claude's response text. */
  text: string
}

/**
 * Call the AI completion endpoint. Always returns {data, error} — never throws.
 */
export async function complete(
  input: CompleteInput,
): Promise<{ data: CompleteResult | null; error: Error | null }> {
  const { data, error } = await supabase.functions.invoke<CompleteResult>(
    'ai-complete',
    { body: input },
  )

  if (error) {
    const ctx = (error as any).context
    let message = error.message
    if (ctx && typeof ctx.text === 'function') {
      try {
        const text = await ctx.text()
        const parsed = JSON.parse(text)
        message = parsed?.error || parsed?.message || message
      } catch {
        /* keep default */
      }
    }
    return { data: null, error: new Error(message) }
  }

  if (!data?.text) {
    return { data: null, error: new Error('No text returned') }
  }

  return { data, error: null }
}
